import {
  getLocalStorageItem,
  getSessionStorageItem,
  navigateToPage,
  setLocalStorageItem,
  setSessionStorageItem,
} from '@marketplace-web/shared/browser'
import {
  CheckoutOrderType,
  HAS_BOUGHT_STORAGE_VALUE,
  SINGLE_CHECKOUT_ORDER_TYPE_STORAGE_KEY,
  SINGLE_CHECKOUT_LOAD_TIME_TRACKING_DETAILS_STORAGE_KEY,
  CheckoutItemModel,
  getUserBuyCount,
} from '@marketplace-web/domain/checkout-data'
import {
  GO_TO_WALLET_URL,
  MEMBER_PROFILE_URL,
  ROOT_URL,
  SINGLE_CHECKOUT_URL,
} from 'constants/routes'
import { UserDto } from '@marketplace-web/shared/user-data'
import { CurrencyAmountModel } from '@marketplace-web/domain/currency-data'

import { GoogleTagManagerEvent, googleTagManagerTrack } from '@marketplace-web/domain/google-track'
import { stringToSha256 } from '@marketplace-web/shared/utils'
import {
  PaymentNavigationDto,
  PaymentNavigationTypes,
} from '@marketplace-web/domain/checkout-payments-options-data'
import { CONVERSATION_URL } from '@marketplace-web/domain/conversation-messaging-data'
import { EscrowOrderTrackingEntrySource } from '@marketplace-web/domain/order-details-data'

export const markSingleCheckoutLoadStartTime = (orderId: number) => {
  const loadTimeStartStamp = new Date().getTime()
  const trackingDetails = {
    orderId,
    loadTimeStartStamp,
  }

  setLocalStorageItem(
    SINGLE_CHECKOUT_LOAD_TIME_TRACKING_DETAILS_STORAGE_KEY,
    JSON.stringify(trackingDetails),
  )
}

const storeCheckoutOrderType = (orderType: string) => {
  setSessionStorageItem(SINGLE_CHECKOUT_ORDER_TYPE_STORAGE_KEY, orderType)
}

export const navigateToSingleCheckout = (
  checkoutId: string,
  orderId: number,
  orderType: string,
) => {
  storeCheckoutOrderType(orderType)
  markSingleCheckoutLoadStartTime(orderId)
  navigateToPage(SINGLE_CHECKOUT_URL(checkoutId, orderId, orderType))
}

export const isValidOrderType = (value: string): value is CheckoutOrderType => {
  return Object.values(CheckoutOrderType).includes(value as CheckoutOrderType)
}

export const resolveOrderType = (
  orderTypeFromUrl: string | undefined,
): CheckoutOrderType | undefined => {
  if (orderTypeFromUrl) {
    return isValidOrderType(orderTypeFromUrl) ? orderTypeFromUrl : undefined
  }

  const savedOrderType = getSessionStorageItem(SINGLE_CHECKOUT_ORDER_TYPE_STORAGE_KEY)

  return !!savedOrderType && isValidOrderType(savedOrderType) ? savedOrderType : undefined
}

export const checkIfFirstPurchase = async (userId: number) => {
  if (getLocalStorageItem(`hasBought-${userId}`) === HAS_BOUGHT_STORAGE_VALUE) return false

  const response = await getUserBuyCount()

  if ('errors' in response) return undefined

  return response.value === 1
}
export const trackPurchase = async (
  user: UserDto,
  totalPrice: CurrencyAmountModel,
  items: Array<CheckoutItemModel>,
  transactionId: number | null,
  trackFunction: typeof googleTagManagerTrack,
) => {
  const { id: userId, email: userEmail } = user
  const isFirstPurchase = await checkIfFirstPurchase(userId)
  const formattedEventId = await stringToSha256(transactionId?.toString() || '')

  const event = {
    event_id: formattedEventId,
    user_email: userEmail,
    value: parseFloat(totalPrice.amount),
    currency: totalPrice.currencyCode,
    items: items.map(item => ({
      item_id: item.id,
      value: parseFloat(item.price.amount),
      currency: item.price.currencyCode,
    })),
  }

  trackFunction(GoogleTagManagerEvent.ItemBuy, event)

  if (isFirstPurchase) {
    trackFunction(GoogleTagManagerEvent.FirstBuy, {
      value: parseFloat(totalPrice.amount),
      currency: totalPrice.currencyCode,
      user_email: userEmail,
    })
  }

  setLocalStorageItem(`hasBought-${userId}`, HAS_BOUGHT_STORAGE_VALUE)
}

export function getPaymentCompletionRedirectUrl(navigation: PaymentNavigationDto) {
  switch (navigation?.type) {
    case PaymentNavigationTypes.Conversation:
      return CONVERSATION_URL(navigation.id, EscrowOrderTrackingEntrySource.Purchase)
    case PaymentNavigationTypes.UserProfile:
      return MEMBER_PROFILE_URL(navigation.id)
    case PaymentNavigationTypes.Wallet:
      return GO_TO_WALLET_URL
    default:
      return ROOT_URL
  }
}
